import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CLAIMED_PHYSICAL,
  FILTER_TAGS,
  FULL_BASE,
  META,
  THUMB_BASE,
  TOTAL,
  TRAITS,
  pad,
} from "./wpnzData";
import WpnzLogo from "./WpnzLogo";

const ALL_NUMBERS = Array.from({ length: TOTAL }, (_, i) => i + 1);
const SATFLOW_URL = "https://www.satflow.com/ordinals/wpnz";
const FIDELIO_URL = "https://x.com/FideliosWorld";

function Tile({ n, isClaimed, onClick }) {
  const [failed, setFailed] = useState(false);
  return (
    <a
      className={`tile${failed ? " tile-fallback" : ""}`}
      href={`#${pad(n)}`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {!failed && (
        <img
          src={`${THUMB_BASE}${pad(n)}.webp`}
          alt={`WPNZ #${pad(n)}`}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      <div className="fallback-num">{pad(n)}</div>
      <div className="label">
        WPNZ #{pad(n)}
        {isClaimed ? " · Physical claimed" : ""}
      </div>
      {isClaimed && <div className="claimed-badge" title="Physical claimed" />}
    </a>
  );
}

function Modal({ n, onClose, onPrev, onNext }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [n]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const meta = META[n] || {};
  const isClaimed = CLAIMED_PHYSICAL.has(n);
  const trait = TRAITS[n] || "—";
  const fullUrl = `${FULL_BASE}${n}.jpg`;
  const ordiscan = meta.inscription
    ? `https://ordiscan.com/inscription/${meta.inscription}`
    : "https://ordiscan.com";
  const shareText = encodeURIComponent(
    `WPNZ #${pad(n)} — 1 of 555. Hand-drawn. Inscribed on bitcoin.`
  );
  const shareUrl = encodeURIComponent(`https://wpnz.art/#${pad(n)}`);
  const twitterShare = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`;

  const handleCopy = (e) => {
    e.preventDefault();
    const url = `https://wpnz.art/#${pad(n)}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="modal open"
      role="dialog"
      aria-modal="true"
      aria-label="WPNZ piece detail"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-inner">
        <button className="modal-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <button className="modal-nav prev" aria-label="Previous" onClick={onPrev}>
          ‹
        </button>
        <button className="modal-nav next" aria-label="Next" onClick={onNext}>
          ›
        </button>

        <div className={`modal-image${imgFailed ? " modal-fallback" : ""}`}>
          <img
            src={fullUrl}
            alt={`WPNZ #${pad(n)}`}
            onError={() => setImgFailed(true)}
          />
          <div className="modal-fallback-num">WPNZ #{pad(n)}</div>
        </div>

        <div className="modal-info">
          <div className="meta-tag">Bitcoin Ordinal · Hitman Sat</div>
          <h2>WPNZ #{pad(n)}</h2>
          <p className="subtitle">
            One of 555 · Hand-drawn by{" "}
            <a href={FIDELIO_URL} target="_blank" rel="noopener noreferrer">
              @FideliosWorld
            </a>
          </p>

          <dl className="modal-data">
            <dt>Trait</dt>
            <dd>{trait}</dd>
            <dt>Physical</dt>
            <dd>
              {isClaimed ? (
                <span style={{ color: "var(--gold)" }}>Claimed ✓</span>
              ) : (
                <span style={{ color: "var(--text-mute)" }}>Not yet claimed</span>
              )}
            </dd>
            <dt>Inscription</dt>
            <dd>{meta.inscription || "—"}</dd>
            <dt>Sat</dt>
            <dd>{meta.sat || "—"}</dd>
            <dt>Owner</dt>
            <dd>{meta.owner || "—"}</dd>
            <dt>Collection</dt>
            <dd>WPNZ · 555 Weapons</dd>
          </dl>

          <div className="modal-actions">
            <a
              href={SATFLOW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta solid satflow"
            >
              Buy on SatFlow →
            </a>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cta"
            >
              View Full Resolution
            </a>
          </div>

          <div className="modal-secondary">
            <a href={ordiscan} target="_blank" rel="noopener noreferrer">
              Ordiscan ↗
            </a>
            <a href={SATFLOW_URL} target="_blank" rel="noopener noreferrer">
              Magisat ↗
            </a>
            <a href={twitterShare} target="_blank" rel="noopener noreferrer">
              Share on X ↗
            </a>
            <a href="#copy" onClick={handleCopy}>
              {copied ? "Copied ✓" : "Copy Link"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [filter, setFilter] = useState("all");
  const [physical, setPhysical] = useState("all");
  const [sort, setSort] = useState("asc");
  const [query, setQuery] = useState("");
  const [modalIdx, setModalIdx] = useState(null);

  const items = useMemo(() => {
    let list = ALL_NUMBERS;
    if (filter !== "all") list = list.filter((n) => TRAITS[n] === filter);
    if (physical === "claimed")
      list = list.filter((n) => CLAIMED_PHYSICAL.has(n));
    else if (physical === "unclaimed")
      list = list.filter((n) => !CLAIMED_PHYSICAL.has(n));
    const q = query.trim();
    if (q !== "")
      list = list.filter((n) => String(n).includes(q) || pad(n).includes(q));
    list = [...list].sort((a, b) => (sort === "asc" ? a - b : b - a));
    return list;
  }, [filter, physical, sort, query]);

  const closeModal = useCallback(() => {
    setModalIdx(null);
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const goPrev = useCallback(() => {
    setModalIdx((i) => {
      if (i === null || items.length === 0) return i;
      return (i - 1 + items.length) % items.length;
    });
  }, [items.length]);

  const goNext = useCallback(() => {
    setModalIdx((i) => {
      if (i === null || items.length === 0) return i;
      return (i + 1) % items.length;
    });
  }, [items.length]);

  useEffect(() => {
    const handleHash = () => {
      const m = window.location.hash.match(/^#(\d+)$/);
      if (!m) return;
      const n = parseInt(m[1], 10);
      const idx = items.indexOf(n);
      if (idx >= 0) setModalIdx(idx);
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, [items]);

  const modalNumber =
    modalIdx !== null && modalIdx >= 0 && modalIdx < items.length
      ? items[modalIdx]
      : null;

  return (
    <>
      <nav className="top">
        <a href="#hero" className="brand">
          <span className="brand-mark">
            <WpnzLogo />
          </span>
          WPNZ
        </a>

        <div className="nav-links">
          <a href="#collection">Collection</a>
          <a href="#provenance">Provenance</a>
          <a
            href={SATFLOW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cta solid satflow"
          >
            Collect on SatFlow →
          </a>
        </div>
      </nav>

      <main>
        <section className="hero" id="hero">
          <div className="hero-logo">
            <WpnzLogo ariaLabel="WPNZ" />
          </div>

          <h1 className="hero-tag">
            Weapons, Bitcoin, and art all represent power and freedom in their own way.
          </h1>

          <p className="hero-body">
            A weapon can protect or destroy. Bitcoin gives people control over their money. Art turns emotion into something the world can feel. None of them belong to big systems. They belong to the people who create them, hold them, and know how to use them. In the right hands, power. In the wrong hands, chaos. That tension between control and chaos, beauty and destruction, is what makes it human.
          </p>

          <div className="hero-ctas">
            <a
              href={SATFLOW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta solid satflow"
            >
              Collect on SatFlow →
            </a>
            <a href="#collection" className="cta ghost">
              View the Collection
            </a>
          </div>

          <div className="hero-stats">
            <span>
              <strong>555</strong> Pieces
            </span>
            <span>
              <strong>Hitman</strong> Sats
            </span>
            <span>
              On <strong>Bitcoin</strong>
            </span>
            <span>
              Art by{" "}
              <strong>
                <a
                  href={FIDELIO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="author-link"
                >
                  @FideliosWorld
                </a>
              </strong>
            </span>
          </div>
        </section>

        <section className="collection" id="collection">
          <div className="section-head">
            <h2>
              The Armory{" "}
              <span className="num">555 total · 133 physicals claimed</span>
            </h2>
            <div className="sort-wrap">
              <span>Physical</span>
              <select
                aria-label="Filter by physical status"
                value={physical}
                onChange={(e) => setPhysical(e.target.value)}
              >
                <option value="all">All</option>
                <option value="claimed">Claimed only</option>
                <option value="unclaimed">Not yet claimed</option>
              </select>
              <span>Sort</span>
              <select
                aria-label="Sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="asc">Number · Ascending</option>
                <option value="desc">Number · Descending</option>
              </select>
            </div>
          </div>

          <div className="filter-bar">
            <input
              type="text"
              placeholder="Search by number (e.g. 433)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="filter-tags">
              {FILTER_TAGS.map((t) => (
                <button
                  key={t.value}
                  className={`tag${filter === t.value ? " active" : ""}`}
                  onClick={() => setFilter(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid">
            {items.length === 0 ? (
              <div className="grid-empty">No pieces match that filter.</div>
            ) : (
              items.map((n, idx) => (
                <Tile
                  key={n}
                  n={n}
                  isClaimed={CLAIMED_PHYSICAL.has(n)}
                  onClick={() => setModalIdx(idx)}
                />
              ))
            )}
          </div>
        </section>

        <section className="provenance" id="provenance">
          <div>
            <div className="label">Provenance</div>
            <h3>Inscribed on Hitman Sats. Permanent, on-chain, unrepeatable.</h3>
            <p>
              Every WPNZ is written directly to the Bitcoin blockchain through the Ordinals protocol. No contract migrations. No platform risk. No custodian. Just the sat, the inscription, and the wallet that holds it.
            </p>
            <p>
              The collection is structured as a parent–child inscription set: a single parent inscription anchors the collection, and all 555 pieces are children inscribed beneath it. Provenance is verifiable on-chain by anyone, forever.
            </p>
            <p>
              The artwork lives at the lowest layer of the internet's most resilient network. It will outlast any website, including this one.
            </p>
          </div>
          <dl className="prov-meta">
            <dt>Protocol</dt>
            <dd>Bitcoin Ordinals</dd>
            <dt>Structure</dt>
            <dd>Parent–Child · 555 children</dd>
            <dt>Supply</dt>
            <dd>555 · Immutable</dd>
            <dt>Sat Type</dt>
            <dd>Hitman</dd>
            <dt>Creator</dt>
            <dd>
              <a
                href={FIDELIO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gold-link"
              >
                @FideliosWorld
              </a>
            </dd>
            <dt>Year</dt>
            <dd>2024 — 2025</dd>
            <dt>Physical</dt>
            <dd>Shipping to holders · Batch #2 TBA</dd>
          </dl>
        </section>
      </main>

      <footer>
        <div className="foot-brand">
          <h4>WPNZ</h4>
          <p>
            555 hand-drawn weapons inscribed on Hitman Sats. An ordinals art collection exploring power, freedom, and the raw edge between protection and destruction.
          </p>
        </div>
        <div>
          <h4>Community</h4>
          <ul>
            <li>
              <a
                href="https://x.com/WPNZbtc"
                target="_blank"
                rel="noopener noreferrer"
              >
                X · @WPNZbtc
              </a>
            </li>
            <li>
              <a
                href="https://discord.com/invite/PMsNKc5q37"
                target="_blank"
                rel="noopener noreferrer"
              >
                Discord
              </a>
            </li>
          </ul>
        </div>
      </footer>

      <div className="foot-bottom">
        <span>© WPNZ · All Rights Reserved</span>
        <div className="socials">
          <a
            href="https://x.com/WPNZbtc"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X"
          >
            <svg viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a
            href="https://discord.com/invite/PMsNKc5q37"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Discord"
          >
            <svg viewBox="0 0 24 24">
              <path d="M20.317 4.37a19.8 19.8 0 0 0-4.885-1.515.07.07 0 0 0-.074.037c-.21.375-.444.864-.608 1.25a18.3 18.3 0 0 0-5.487 0 12.5 12.5 0 0 0-.617-1.25.077.077 0 0 0-.074-.037A19.7 19.7 0 0 0 3.683 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.08.08 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.1 14.1 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13 13 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10 10 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01q.18.147.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107q.538 1.045 1.226 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.42 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.42 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.946 2.42-2.157 2.42z" />
            </svg>
          </a>
        </div>
      </div>

      {modalNumber !== null && (
        <Modal
          n={modalNumber}
          onClose={closeModal}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </>
  );
}
